import { ArrowLeft, Inbox, ListChecks, Trash2 } from 'lucide-react'
import { CamposDeDevolucion } from '@/features/rh/components/recorrido/CamposDeDevolucion'
import type { Candidate } from '@/services/api'
import type { PasoRecorrido } from '@/shared/components/Recorrido'

/**
 * Recorrido de la bandeja de descartados, en el panel de reclutamiento.
 *
 * Sale solo la primera vez que alguien entra a la pantalla, y de ahí en
 * adelante se abre desde el botón "Ver recorrido" que está junto al título.
 */

// La marca va por usuario y no por navegador: en un equipo compartido, quien
// entra por primera vez necesita el recorrido aunque el anterior ya lo haya
// visto.
const clave = (userId: string) => `recorridoDescartados.visto.${userId}`

export function yaVioElRecorrido(userId: string): boolean {
  try {
    return localStorage.getItem(clave(userId)) === '1'
  } catch {
    // Si el almacenamiento está bloqueado se asume que ya lo vio: vale más
    // dejar trabajar que abrir un tutorial en cada carga.
    return true
  }
}

export function guardarRecorridoVisto(userId: string): void {
  try {
    localStorage.setItem(clave(userId), '1')
  } catch {
    // Volverá a salir en la próxima carga. Molesta, pero no rompe nada.
  }
}

/**
 * Filas de ejemplo para los pasos que senalan la tabla.
 *
 * La primera vez que alguien entra la bandeja esta vacia —o con un solo
 * descartado—, y ahi no se alcanza a ver donde queda el boton de devolver ni
 * para que sirven las casillas. Durante el recorrido se rellena hasta cinco y
 * al salir desaparecen: no se guardan ni se piden al API, viven en memoria.
 *
 * El id lleva prefijo `ejemplo-` para que se distingan de un candidato real de
 * un vistazo; de todas formas la capa del recorrido no deja hacerles clic.
 *
 * Solo se declaran los campos que pinta la tabla, de ahi el cast: armar un
 * Candidate completo pediria treinta campos que esta vista ni mira.
 */
const CAMPOS_DE_EJEMPLO = [
  {
    fullName: 'Alejandra Ramirez Lopez',
    email: 'alejandra.rl@example.com',
    descarteEtapa: 'entrevista_rh',
    descarteComentario: 'Perfil solido, pero pidio un sueldo fuera de rango.',
  },
  {
    fullName: 'Marco Antonio Rivas',
    email: 'marco.rivas@example.com',
    descarteEtapa: 'pre_seleccionado',
    descarteComentario: 'Acepto otra oferta antes de la entrevista final.',
  },
  {
    fullName: 'Lucia Fernandez del Pozo',
    email: 'lucia.fdez@example.com',
    descarteEtapa: 'en_proceso',
    descarteComentario: null,
  },
  {
    fullName: 'Jorge Salas Miranda',
    email: 'jorge.salas@example.com',
    descarteEtapa: 'en_proceso',
    descarteComentario: 'No cubre la experiencia en obra que pide la vacante.',
  },
  {
    fullName: 'Daniela Ortega Vidal',
    email: 'daniela.ortega@example.com',
    descarteEtapa: 'pre_seleccionado',
    descarteComentario: null,
  },
]

export const DESCARTADOS_DE_EJEMPLO = CAMPOS_DE_EJEMPLO.map((c, i) => ({
  ...c,
  id: `ejemplo-recorrido-${i + 1}`,
  descarteMotivo: null,
  descartadoPor: null,
  descartadoEn: null,
})) as unknown as Candidate[]

/**
 * Los siete pasos. Reciben lo que necesitan para dejar la pantalla en el estado
 * de cada paso: los que señalan la tabla necesitan la pestaña de Descartados
 * abierta, y el de vaciar necesita además una casilla marcada, porque ese botón
 * solo existe cuando hay selección.
 */
export function crearPasosDescartados({
  irADescartados,
  marcarPrimero,
}: {
  irADescartados: () => void
  marcarPrimero: () => void
}): PasoRecorrido[] {
  return [
    {
      titulo: 'Descartar a un candidato no lo elimina',
      cuerpo: (
        <>
          Cuando descartas a alguien sale del tablero Kanban, pero se guarda en la{' '}
          <strong className="font-semibold text-gray-800">pestaña Descartados.</strong>{' '}
          Desde ahí puedes devolverlo al proceso o borrarlo definitivamente. Te lo
          muestro en 30 segundos.
        </>
      ),
    },
    {
      titulo: 'Aquí viven los candidatos descartados',
      cuerpo:
        'Todos conservan su contacto, la vacante a la que aplicaron, la etapa en la que estaban y el motivo del descarte. El número junto a la pestaña te dice cuántos hay en la bandeja.',
      icono: Inbox,
      ancla: 'tab-descartados',
    },
    {
      titulo: 'Devuelve a un candidato al tablero',
      cuerpo:
        'Esta acción lo reactiva y vuelve a aparecer en el Kanban. Úsala cuando se abre otra vacante o cuando el descarte fue un error.',
      icono: ArrowLeft,
      ancla: 'devolver-fila',
      preparar: irADescartados,
    },
    {
      titulo: 'Tú decides en qué columna reaparece',
      cuerpo:
        'Al devolverlo eliges la vacante y la columna de destino. Sin revisión lo regresa al inicio del proceso, sin la evaluación previa; volver a su etapa conserva el avance que ya tenía. El motivo de la reactivación es opcional, pero queda en el historial del candidato.',
      // En vez de abrir el modal de un candidato real, el paso ensena los dos
      // campos como muestra debajo de la tarjeta: se explica igual y nadie
      // corre el riesgo de reactivar a alguien por accidente.
      panel: <CamposDeDevolucion />,
      preparar: irADescartados,
    },
    {
      // El diseño hablaba de una barra para devolver o eliminar en masa. Hoy la
      // selección solo habilita eliminar, y devolver es fila por fila, así que
      // la copia dice lo que la pantalla hace.
      titulo: 'También puedes eliminar a varios a la vez',
      cuerpo:
        'Marca las casillas de los candidatos que quieras y aparece el botón para eliminarlos en una sola operación. Devolver al tablero sigue siendo uno por uno, desde su fila.',
      icono: ListChecks,
      ancla: 'seleccionar-todos',
      preparar: irADescartados,
    },
    {
      // Igual que el paso anterior: el diseño decía "vaciar toda la bandeja" y
      // mencionaba conservar los recientes y exportar. Nada de eso existe; el
      // botón elimina los seleccionados.
      titulo: 'Elimínalos cuando ya no los necesites',
      cuerpo:
        'El botón aparece en cuanto marcas al menos uno y los borra de forma definitiva: ya no podrás contactarlos ni devolverlos al tablero.',
      icono: Trash2,
      ancla: 'vaciar',
      preparar: () => {
        irADescartados()
        // Sin selección ese botón no está en pantalla y el paso se quedaría sin
        // qué señalar, así que el recorrido marca el primero.
        marcarPrimero()
      },
    },
    {
      titulo: 'Eso es todo',
      cuerpo:
        'Descartar es reversible mientras el candidato siga en la bandeja. Puedes volver a ver este recorrido desde el botón Ver recorrido, junto al título.',
      imagen: '/success-logo.svg',
    },
  ]
}
